
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSmsProviders } from "@/hooks/useSmsProviders";
import { useTwilioSettings } from "@/hooks/useTwilioSettings";
import { useRingCentralSettings } from "@/hooks/useRingCentralSettings";
import { Loader2, MessageSquare, CheckCircle2, AlertTriangle } from "lucide-react";

const SmsProviderSelector = () => {
  const { providers, loading, setActiveProvider } = useSmsProviders();
  const { settings: twilioSettings } = useTwilioSettings();
  const { settings: ringcentralSettings } = useRingCentralSettings();

  const activeProvider = providers.find(p => p.is_active);

  const getProviderStatus = (providerName: string) => {
    if (providerName === 'twilio') {
      return twilioSettings?.is_active ? 'configured' : 'not_configured';
    }
    if (providerName === 'ringcentral') {
      return ringcentralSettings?.is_active ? 'configured' : 'not_configured';
    }
    return 'not_configured';
  };

  const handleProviderChange = async (providerName: string) => {
    const status = getProviderStatus(providerName);
    if (status === 'not_configured') {
      return; // Don't allow selection of unconfigured providers
    }

    await setActiveProvider(providerName);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
          SMS provider
        </CardTitle>
        <CardDescription>
          Pick which service sends your texts. Only a configured provider can be made active.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {providers.map((provider) => {
            const status = getProviderStatus(provider.provider_name);
            const isActive = provider.is_active;
            const isConfigured = status === 'configured';

            return (
              <div
                key={provider.id}
                className={`rounded-lg border p-4 transition-colors ${
                  isActive
                    ? 'border-status-success/40 bg-status-success/5'
                    : isConfigured
                      ? 'border-border bg-card hover:border-status-info/40'
                      : 'border-border bg-muted/40'
                }`}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="font-medium capitalize text-foreground">
                    {provider.provider_name}
                  </h3>
                  {isActive ? (
                    <Badge variant="outline" className="gap-1 border-status-success/30 text-status-success">
                      <CheckCircle2 className="h-3 w-3" />
                      Active
                    </Badge>
                  ) : isConfigured ? (
                    <Badge variant="outline" className="border-status-info/30 text-status-info">Ready</Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1 border-status-warning/30 text-status-warning">
                      <AlertTriangle className="h-3 w-3" />
                      Not set up
                    </Badge>
                  )}
                </div>

                <p className="mb-3 text-sm text-muted-foreground">
                  {isActive
                    ? "Sending your notifications now."
                    : isConfigured
                      ? "Configured and ready to switch to."
                      : "Add credentials below to use this provider."}
                </p>

                <Button
                  variant={isActive ? "secondary" : isConfigured ? "default" : "outline"}
                  onClick={() => handleProviderChange(provider.provider_name)}
                  disabled={!isConfigured || isActive}
                  className="h-11 w-full"
                >
                  {isActive ? "Currently active" : isConfigured ? "Use this provider" : "Set up first"}
                </Button>
              </div>
            );
          })}
        </div>

        {!activeProvider && (
          <div className="flex items-start gap-3 rounded-lg border border-status-warning/30 bg-status-warning/5 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-status-warning" />
            <div className="text-sm">
              <p className="font-medium text-foreground">No provider is active</p>
              <p className="mt-0.5 text-muted-foreground">
                Configure a provider below and select it here, or customers will not receive texts.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmsProviderSelector;
